import { useState } from 'react';
import {
  Activity, Send, ShieldCheck, Sparkles, Languages, Check, ArrowRight,
  Zap, Bell, BarChart3, Clock, Telescope, Lock, ExternalLink,
} from 'lucide-react';
import axios from 'axios';
import SEO from '../components/SEO';
import LocaleLink from '../components/LocaleLink';
import { useLanguage } from '../contexts/LanguageContext';
import './quidanalyzer.css';

const TELEGRAM_BOT_URL = process.env.REACT_APP_TELEGRAM_BOT_URL || 'https://t.me/Murphi707_bot';
const API = process.env.REACT_APP_BACKEND_URL;

const t = {
  ar: {
    badge: 'Quidanalyzer · مباشر',
    h1: 'ذكاء كريبتو متعدد اللغات',
    h1b: 'مباشرة على تيليجرام',
    sub: 'إشارات وتحليلات وتنبيهات سوقية لحظية بالعربية والإنجليزية، يوصلها بوت Quidanalyzer مباشرة إلى محادثاتك. تشغيل خفيف، ذكاء عميق.',
    launchBot: 'افتح البوت على تيليجرام',
    subscribePremium: 'اشترك في النسخة المميزة',
    why: {
      h: 'ماذا يقدّم Quidanalyzer',
      cards: [
        { icon: Activity,  title: 'إشارات لحظية',    body: 'نبض السوق لحظة حدوثه — كسر مقاومة، تراكم، تحويلات حيتان.' },
        { icon: Languages, title: 'عربي + إنجليزي',  body: 'كل تنبيه يصلك بلغتك. تبديل فوري داخل البوت.' },
        { icon: BarChart3, title: 'تحليل عميق',       body: 'مخططات وقراءات يومية لأهم العملات — لا مجرد أسعار.' },
        { icon: Bell,      title: 'تنبيهات مخصصة',    body: 'تنبيهات سعر، حجم، وحركة محافظ تختارها أنت.' },
        { icon: ShieldCheck,title: 'مصادر موثوقة',     body: 'دمج بيانات من مصادر متعددة + طبقة ذكاء اصطناعي للتحقق.' },
        { icon: Clock,      title: 'يعمل ٢٤/٧',        body: 'البنية التحتية لا تنام، حتى حين تنام أنت.' },
      ],
    },
    plans: {
      h: 'الخطة',
      sub: 'ابدأ مجاناً، أو افتح كل الإمكانيات بـ ١٥ دولاراً شهرياً.',
      free: {
        name: 'مجاناً',
        price: '٠$',
        per: '/شهرياً',
        list: [
          'تنبيهات سوقية يومية',
          'تحليل عام لأهم العملات',
          'التبديل بين العربية والإنجليزية',
          'دعم عبر تيليجرام',
        ],
        cta: 'افتح البوت',
        ctaSub: 'بدون بطاقة',
      },
      premium: {
        name: 'مميّز',
        price: '١٥$',
        per: '/شهرياً',
        tag: 'الأكثر شعبية',
        list: [
          'كل ميزات المجاني',
          'إشارات لحظية بدون تأخير',
          'تنبيهات مخصصة بلا حدود',
          'مخططات وتحليلات تفصيلية',
          'وصول مبكر للميزات الجديدة',
          'دعم ذو أولوية',
        ],
        cta: 'اشترك الآن',
        ctaSub: 'يمكنك الإلغاء في أي وقت',
      },
    },
    flow: {
      h: 'كيف يعمل',
      steps: [
        { n: '٠١', title: 'اشترك', body: 'أكمل الدفع الآمن عبر سترايب — لا حساب جديد، لا كلمة سر.' },
        { n: '٠٢', title: 'افتح البوت', body: 'ادخل @Murphi707_bot على تيليجرام واكتب /start.' },
        { n: '٠٣', title: 'استلم الذكاء', body: 'تبدأ الإشارات والتنبيهات بالوصول إليك مباشرة.' },
      ],
    },
    faq: {
      h: 'أسئلة سريعة',
      items: [
        { q: 'هل أحتاج حساب جديد؟', a: 'لا. الدفع عبر سترايب، والوصول مباشر من تيليجرام.' },
        { q: 'هل يمكنني الإلغاء؟', a: 'نعم في أي وقت من صفحة "إدارة الاشتراك" — تبقى مشتركاً حتى نهاية الدورة المدفوعة.' },
        { q: 'هل البيانات آمنة؟', a: 'كل المدفوعات تتم على بنية سترايب. لا نخزّن بيانات بطاقتك.' },
        { q: 'هل يدعم اللغتين دائماً؟', a: 'نعم — تبديل فوري عبر أمر /lang داخل البوت.' },
      ],
    },
    secure: 'مدفوعات آمنة عبر سترايب · اشتراك شهري · إلغاء في أي وقت',
  },
  en: {
    badge: 'Quidanalyzer · Live',
    h1: 'Multilingual crypto intelligence',
    h1b: 'delivered on Telegram',
    sub: 'Real-time crypto signals, analysis and market alerts in Arabic and English — pushed straight into your Telegram by the Quidanalyzer bot. Lightweight to run, deep on intelligence.',
    launchBot: 'Open bot on Telegram',
    subscribePremium: 'Subscribe to Premium',
    why: {
      h: 'What Quidanalyzer gives you',
      cards: [
        { icon: Activity,   title: 'Real-time signals',  body: 'Market heartbeats the moment they happen — breakouts, accumulation, whale flows.' },
        { icon: Languages,  title: 'Arabic + English',   body: 'Every alert in your language. Toggle live inside the bot.' },
        { icon: BarChart3,  title: 'Deep analysis',      body: 'Daily readings and charts on the assets that matter — not just prices.' },
        { icon: Bell,       title: 'Custom alerts',      body: 'Price, volume, and wallet-movement alerts you define.' },
        { icon: ShieldCheck,title: 'Trusted sources',    body: 'Multi-source aggregation + AI verification layer.' },
        { icon: Clock,      title: '24/7 operation',     body: 'The infrastructure never sleeps, even when you do.' },
      ],
    },
    plans: {
      h: 'Plans',
      sub: 'Start free, or unlock everything for $15 / month.',
      free: {
        name: 'Free',
        price: '$0',
        per: '/month',
        list: [
          'Daily market alerts',
          'General analysis of top assets',
          'Arabic / English toggle',
          'Support via Telegram',
        ],
        cta: 'Open bot',
        ctaSub: 'No card needed',
      },
      premium: {
        name: 'Premium',
        price: '$15',
        per: '/month',
        tag: 'Most popular',
        list: [
          'Everything in Free',
          'Real-time signals — no delay',
          'Unlimited custom alerts',
          'In-depth charts & analyses',
          'Early access to new features',
          'Priority support',
        ],
        cta: 'Subscribe now',
        ctaSub: 'Cancel anytime',
      },
    },
    flow: {
      h: 'How it works',
      steps: [
        { n: '01', title: 'Subscribe', body: 'Secure Stripe checkout — no new account, no password.' },
        { n: '02', title: 'Open the bot', body: 'Hit @Murphi707_bot on Telegram and type /start.' },
        { n: '03', title: 'Get intelligence', body: 'Signals and alerts start flowing into your chat.' },
      ],
    },
    faq: {
      h: 'Quick FAQ',
      items: [
        { q: 'Do I need a new account?', a: 'No. Payment is via Stripe, access is straight from Telegram.' },
        { q: 'Can I cancel?', a: 'Yes, anytime from the "Manage subscription" page. You stay subscribed through the end of the current period.' },
        { q: 'Is my data safe?', a: 'All payments run on Stripe infrastructure — we never see or store your card data.' },
        { q: 'Always bilingual?', a: 'Yes — switch with /lang inside the bot, instantly.' },
      ],
    },
    secure: 'Secure payments by Stripe · Monthly subscription · Cancel anytime',
  },
};

const QuidanalyzerPage = () => {
  const { language, isRTL } = useLanguage();
  const c = t[language];
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  const subscribe = async () => {
    setLoading(true);
    setErrMsg('');
    try {
      const res = await axios.post(`${API}/api/quidanalyzer/checkout`, {
        lang: language,
        origin: window.location.origin,
      });
      if (res?.data?.url) {
        window.location.href = res.data.url;
      } else {
        setErrMsg(language === 'ar' ? 'تعذر بدء عملية الدفع. حاول مرة أخرى.' : 'Could not start checkout. Please try again.');
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErrMsg(
        detail ||
        (language === 'ar'
          ? 'خدمة الدفع غير متاحة حالياً.'
          : 'Payment service unavailable right now.')
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        titleEn="Quidanalyzer — Multilingual Crypto Intelligence on Telegram | Quidory"
        titleAr="Quidanalyzer — ذكاء كريبتو متعدد اللغات على تيليجرام | كويدوري"
        descEn="Real-time crypto signals, charts, and bilingual market alerts (Arabic + English), delivered straight to your Telegram by the Quidanalyzer bot. Subscribe for $15/mo."
        descAr="إشارات وتحليلات وتنبيهات سوقية لحظية بالعربية والإنجليزية، يوصلها بوت Quidanalyzer إلى تيليجرام مباشرة. اشترك بـ ١٥ دولاراً شهرياً."
        path="/quidanalyzer"
      />

      <div className="pt-16">
        {/* HERO */}
        <section className="relative overflow-hidden border-b border-border bg-background">
          <div className="absolute inset-0 qa-grid-bg opacity-30" />
          <div className="absolute -top-32 -end-32 w-[520px] h-[520px] rounded-full bg-emerald-500/15 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-32 -start-32 w-[420px] h-[420px] rounded-full bg-indigo-500/15 blur-3xl pointer-events-none" />

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24 lg:py-28">
            <div className="grid lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-7 space-y-6">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/5">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                  </span>
                  <span className="text-[11px] font-semibold tracking-widest uppercase text-emerald-300">{c.badge}</span>
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-foreground leading-[1.05]">
                  <span className="block">{c.h1}</span>
                  <span className="block mt-1 bg-gradient-to-r from-emerald-300 via-emerald-400 to-emerald-200 bg-clip-text text-transparent">
                    {c.h1b}
                  </span>
                </h1>
                <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl">
                  {c.sub}
                </p>
                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <button
                    data-testid="qa-subscribe-hero-btn"
                    onClick={subscribe}
                    disabled={loading}
                    className="group inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-emerald-500 text-emerald-950 text-sm font-semibold hover:bg-emerald-400 disabled:opacity-60 disabled:cursor-not-allowed transition-all hover:-translate-y-px shadow-[0_15px_40px_-10px_rgba(16,185,129,0.5)]"
                  >
                    {loading
                      ? (language === 'ar' ? 'جارٍ التحويل...' : 'Redirecting...')
                      : c.subscribePremium}
                    <ArrowRight className={`h-4 w-4 transition-transform group-hover:translate-x-0.5 ${isRTL ? 'rotate-180 group-hover:-translate-x-0.5' : ''}`} />
                  </button>
                  <a
                    href={TELEGRAM_BOT_URL}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="qa-launch-bot-btn"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg border border-border bg-card text-foreground text-sm font-medium hover:bg-muted/50 transition-colors"
                  >
                    <Send className="h-4 w-4 text-emerald-400" />
                    {c.launchBot}
                    <ExternalLink className="h-3.5 w-3.5 text-muted-foreground/60" />
                  </a>
                </div>
                {errMsg && (
                  <p data-testid="qa-error" className="text-xs text-red-400 mt-2">
                    {errMsg}
                  </p>
                )}
                <p className="text-xs text-muted-foreground/70 inline-flex items-center gap-2 pt-2">
                  <Lock className="h-3 w-3" /> {c.secure}
                </p>
              </div>

              {/* Floating bot card */}
              <div className="lg:col-span-5">
                <div className="relative">
                  <div className="absolute -inset-6 bg-gradient-to-br from-emerald-500/20 via-emerald-500/10 to-transparent rounded-3xl blur-2xl" />
                  <div className="relative rounded-2xl border border-emerald-500/20 bg-card/80 backdrop-blur-xl p-5 sm:p-6 qa-glow-emerald">
                    <div className="flex items-center gap-3 pb-4 border-b border-border/60">
                      <div className="relative">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 grid place-items-center text-emerald-950 font-bold">
                          Q
                        </div>
                        <span className="absolute -bottom-0.5 -end-0.5 h-3 w-3 rounded-full bg-emerald-400 ring-2 ring-card" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground truncate">Quidanalyzer Bot</p>
                        <p className="text-[10px] text-emerald-400/80 font-mono">@Murphi707_bot · online</p>
                      </div>
                    </div>
                    <div className="space-y-2 py-4">
                      <div className="text-[11px] inline-block px-3 py-2 rounded-2xl rounded-bl-sm bg-muted/40 text-foreground/90 max-w-[85%]" dir="ltr">
                        BTC just broke <strong>$95,420</strong> · breakout signal
                      </div>
                      <div className="text-[11px] inline-block px-3 py-2 rounded-2xl rounded-bl-sm bg-muted/40 text-foreground/90 max-w-[85%]" dir="rtl">
                        <strong>إيثريوم</strong> · نموذج تراكم على إطار ٤س — متابعة
                      </div>
                      <div className="text-[11px] inline-block px-3 py-2 rounded-2xl rounded-bl-sm bg-emerald-500/15 border border-emerald-500/30 text-emerald-200 max-w-[85%]" dir="ltr">
                        <Sparkles className="inline h-3 w-3 me-1" />
                        Premium alert · whale wallet net-buying past 24h
                      </div>
                    </div>
                    <div className="flex items-center gap-2 pt-2 border-t border-border/60 text-[10px] text-muted-foreground/70">
                      <Languages className="h-3 w-3 text-emerald-400" />
                      AR · EN
                      <span className="text-border/60">·</span>
                      <Telescope className="h-3 w-3" /> live
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* WHY */}
        <section className="py-20 lg:py-24 bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <h2 className="text-3xl sm:text-4xl font-light text-foreground">{c.why.h}</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {c.why.cards.map((card, i) => (
                <div
                  key={i}
                  data-testid={`qa-feature-${i}`}
                  className="group relative p-6 rounded-xl border border-border bg-background/50 hover:border-emerald-500/40 transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="inline-flex p-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 mb-4">
                    <card.icon className="h-4 w-4 text-emerald-400" />
                  </div>
                  <h3 className="text-base font-semibold text-foreground mb-1.5">{card.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{card.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* PLANS */}
        <section className="py-20 lg:py-24 bg-background border-b border-border">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <h2 className="text-3xl sm:text-4xl font-light text-foreground">{c.plans.h}</h2>
              <p className="mt-3 text-sm text-muted-foreground">{c.plans.sub}</p>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              {/* Free */}
              <div
                data-testid="qa-plan-free"
                className="p-7 rounded-2xl border border-border bg-card flex flex-col"
              >
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{c.plans.free.name}</p>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="text-4xl font-light text-foreground" dir="ltr">{c.plans.free.price}</span>
                  <span className="text-sm text-muted-foreground">{c.plans.free.per}</span>
                </div>
                <ul className="mt-6 space-y-2.5 flex-1">
                  {c.plans.free.list.map((it, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-foreground/85">
                      <Check className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href={TELEGRAM_BOT_URL}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="qa-plan-free-cta"
                  className="mt-7 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg border border-border bg-background text-foreground text-sm font-medium hover:bg-muted/50 transition-colors"
                >
                  <Send className="h-4 w-4 text-emerald-400" />
                  {c.plans.free.cta}
                </a>
                <p className="mt-2 text-[10px] text-muted-foreground/70 text-center">{c.plans.free.ctaSub}</p>
              </div>

              {/* Premium */}
              <div
                data-testid="qa-plan-premium"
                className="relative p-7 rounded-2xl border-2 border-emerald-500/50 bg-gradient-to-b from-emerald-500/10 to-card flex flex-col qa-glow-emerald"
              >
                <span className="absolute -top-2.5 start-1/2 -translate-x-1/2 rtl:translate-x-1/2 px-3 py-1 rounded-full bg-emerald-500 text-emerald-950 text-[10px] font-bold uppercase tracking-widest">
                  {c.plans.premium.tag}
                </span>
                <p className="text-xs font-semibold uppercase tracking-widest text-emerald-300">{c.plans.premium.name}</p>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="text-4xl font-light text-foreground" dir="ltr">{c.plans.premium.price}</span>
                  <span className="text-sm text-muted-foreground">{c.plans.premium.per}</span>
                </div>
                <ul className="mt-6 space-y-2.5 flex-1">
                  {c.plans.premium.list.map((it, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                      <Check className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
                <button
                  data-testid="qa-plan-premium-cta"
                  onClick={subscribe}
                  disabled={loading}
                  className="mt-7 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-emerald-500 text-emerald-950 text-sm font-semibold hover:bg-emerald-400 disabled:opacity-60 disabled:cursor-not-allowed transition-all hover:-translate-y-px"
                >
                  {loading
                    ? (language === 'ar' ? 'جارٍ التحويل...' : 'Redirecting...')
                    : c.plans.premium.cta}
                  <Zap className="h-4 w-4" />
                </button>
                <p className="mt-2 text-[10px] text-muted-foreground/70 text-center">{c.plans.premium.ctaSub}</p>
              </div>
            </div>
            <p className="mt-8 text-center text-xs text-muted-foreground/70 inline-flex w-full items-center justify-center gap-2">
              <Lock className="h-3 w-3" /> {c.secure}
            </p>
          </div>
        </section>

        {/* FLOW */}
        <section className="py-20 lg:py-24 bg-card border-b border-border">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-light text-foreground mb-10 text-center">{c.flow.h}</h2>
            <ol className="grid md:grid-cols-3 gap-5">
              {c.flow.steps.map((s, i) => (
                <li
                  key={i}
                  data-testid={`qa-flow-${i}`}
                  className="p-6 rounded-xl border border-border bg-background/50"
                >
                  <p className="text-xs font-mono text-emerald-400 mb-3" dir="ltr">{s.n}</p>
                  <h3 className="text-base font-semibold text-foreground mb-1.5">{s.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{s.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 lg:py-24 bg-background">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-light text-foreground mb-8 text-center">{c.faq.h}</h2>
            <div className="space-y-3">
              {c.faq.items.map((item, i) => (
                <details
                  key={i}
                  data-testid={`qa-faq-${i}`}
                  className="group p-5 rounded-xl border border-border bg-card transition-colors hover:border-emerald-500/30"
                >
                  <summary className="cursor-pointer list-none flex items-center justify-between gap-3 text-sm font-medium text-foreground">
                    <span>{item.q}</span>
                    <span className="text-emerald-400 group-open:rotate-45 transition-transform text-lg leading-none">+</span>
                  </summary>
                  <p className="mt-3 text-xs text-muted-foreground leading-relaxed">{item.a}</p>
                </details>
              ))}
            </div>
            <div className="mt-10 pt-8 border-t border-border text-center text-xs text-muted-foreground/70">
              <LocaleLink to="/quidanalyzer/account" className="text-emerald-400 hover:text-emerald-300 underline-offset-2 hover:underline" data-testid="qa-manage-link">
                {language === 'ar' ? 'إدارة اشتراك حالي' : 'Manage an existing subscription'}
              </LocaleLink>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default QuidanalyzerPage;
